import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { PageHeader, Loading } from "@/components/Common";
import api from "@/lib/api";

function Ring({ value, size = 200, stroke = 12 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <defs>
        <linearGradient id="goldRing" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#AA771C" />
          <stop offset="50%" stopColor="#FCF6BA" />
          <stop offset="100%" stopColor="#D4AF37" />
        </linearGradient>
      </defs>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
      <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="url(#goldRing)" strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={c} initial={{ strokeDashoffset: c }}
        animate={{ strokeDashoffset: offset }} transition={{ duration: 1.4, ease: "easeOut" }} />
    </svg>
  );
}

function Bar({ label, value }) {
  return (
    <div data-testid={`score-component-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="font-sans text-[12px] text-zinc-300">{label}</span>
        <span className="font-mono text-sm font-semibold text-white">{value}<span className="text-zinc-600">/100</span></span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-white/5">
        <motion.div initial={{ width: 0 }} animate={{ width: `${value}%` }} transition={{ duration: 1, ease: "easeOut" }}
          className="h-full rounded-full" style={{ background: "linear-gradient(90deg,#997A15,#D4AF37,#FCF6BA)" }} />
      </div>
    </div>
  );
}

export default function DeluxeScore() {
  const { data, isLoading } = useQuery({ queryKey: ["score"], queryFn: () => api.get("/score").then((r) => r.data) });
  if (isLoading || !data) return <Loading />;
  const c = data.components || {};
  const labels = {
    performance: "Performance", risk: "Risk Management", consistency: "Consistency",
    discipline: "Discipline", execution: "Execution", drawdown_control: "Drawdown Control",
  };
  const rating = data.total >= 80 ? "Elite" : data.total >= 65 ? "Solido" : data.total >= 50 ? "In Crescita" : "Da Migliorare";

  return (
    <div data-testid="score-page">
      <PageHeader title="DELUXE Score" subtitle="Punteggio complessivo 0–100 — oltre il solo profitto" />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="dlx-card dlx-grain flex flex-col items-center justify-center p-8" data-testid="deluxe-score-card">
          <div className="relative flex items-center justify-center">
            <Ring value={data.total} size={220} />
            <div className="absolute flex flex-col items-center">
              <motion.span initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5 }}
                className="font-mono text-6xl font-bold text-white">{data.total}</motion.span>
              <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-zinc-500">/ 100</span>
            </div>
          </div>
          <div className="mt-6 rounded-full border border-gold/30 bg-gold/5 px-5 py-1.5">
            <span className="dlx-gold-text font-serif text-lg">{rating}</span>
          </div>
        </div>

        <div className="dlx-card p-6 sm:p-8">
          <div className="mb-6 font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Componenti</div>
          <div className="space-y-5">
            {Object.entries(labels).map(([k, l]) => <Bar key={k} label={l} value={c[k] ?? 0} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
